/**
 * Which dictionaries the RUNNING BUNDLE carries, and why the browser's bundle carries one.
 *
 * ── The bill this module exists to stop paying ─────────────────────────────────────────────────────────
 *
 * `t()` used to read {@link ../locales!BUNDLED} directly. That single import put all TWENTY-SIX
 * dictionaries into every chunk that translates anything — and the storefront translates on every page, so
 * every shopper downloaded them all. Measured on preprod 2026-09-15: `translate.*.js`, 797 KB raw and
 * 211 KB over the wire, of which the dictionaries were essentially the whole file. A French shop was
 * sending its visitors Arabic, Greek and Macedonian to render a French page.
 *
 * ── The shape that fixes it ────────────────────────────────────────────────────────────────────────────
 *
 * French is imported from its own module, not through the barrel, so bundlers pull in that file and no
 * other. It is the FLOOR every lookup ends at ({@link ../translate!t} step 3), so it has to be present
 * wherever `t()` runs, and it is the one language that is never wrong to have.
 *
 * Every OTHER language reaches a lookup through this registry, and the server fills it: `@aquilixshop/i18n/bundled`
 * is imported by the Astro layer, which runs in Node where the twenty-six cost nothing. In the browser the
 * registry stays empty by design — a non-French page carries its own resolved dictionary in the page's
 * `<script id="aqx-i18n">` block instead (see {@link ../payload!TextsPayload}), which is ~9 KB gzipped for
 * the ONE language that page is written in.
 *
 * ── Module state, and why it is allowed here ───────────────────────────────────────────────────────────
 *
 * {@link ../page-dictionary} forbids module-level state in the strongest terms, because the storefront is
 * one Node process serving every shop and per-shop state would leak between concurrent requests. This
 * registry holds no per-shop anything: it is the same twenty-six constants for every request, for the life
 * of the process. Registering twice with the same map is a no-op, which is what makes it safe to call from
 * a module that several entry points import.
 */
import { FALLBACK_LOCALE } from './fallback';
import { fr } from './locales/fr';
import { readPageTexts } from './page-block';
let registered;
/**
 * Hand the registry the full set of bundled dictionaries. Called by `@aquilixshop/i18n/bundled`, which only
 * server-side code imports.
 */
export function registerBundledDictionaries(all) {
    registered = all;
}
/**
 * Add ONE dictionary to the registry, keeping whatever is already there.
 *
 * For a BROWSER host that renders an arbitrary shop's blocks and learns its language at runtime — the
 * builder canvas — which fetches that one dictionary's chunk (`@aquilixshop/i18n/lazy-bundled`) instead of
 * carrying all twenty-six. Additive rather than replacing, so two stores visited in one session both stay
 * translatable, and so it can never subtract from a server-side `registerBundledDictionaries` call.
 */
export function addBundledDictionary(locale, dictionary) {
    // Seeded with French when nothing is registered yet: the mere ACT of registering must not make the
    // floor of every lookup unresolvable for a caller that asks for it by name.
    registered = { ...(registered ?? { [FALLBACK_LOCALE]: fr }), [locale]: dictionary };
}
/**
 * The bundled dictionary for [locale]: the registered one on the server, French when [locale] IS French,
 * and — in the browser, for any other language — the dictionary THIS PAGE carries.
 *
 * That last step is not a nicety, it is the whole reason a foreign shop still reads its own language.
 * Plenty of callers translate with a context that names a locale and carries no sentences:
 * `applyDefaults` resolves a section field's `defaultKey` as `t({ locale }, key)` (see ui-react's
 * `document/hydrate.ts`), and it does so in the browser, where the cart drawer builds its document. While
 * the bundle carried all twenty-six dictionaries those calls resolved by accident. Dropping twenty-five of
 * them turned an Arabic shop's cart drawer French — caught in a browser walk on 2026-09-15, before this
 * step existed. The page's block is the browser's copy of its own language, so it answers here too.
 *
 * Only for the page's OWN locale: a browser asking for a language that is neither French nor the page's
 * gets nothing, and `t()` falls through to French, which is the contract it always had.
 */
export function bundledDictionary(locale) {
    // `??` rather than an early return: a partially-filled registry (the builder registers the ONE store
    // language it fetched) must still fall through to the steps below for every OTHER locale, exactly as an
    // empty one does. A server registry answers here for all twenty-six and never reaches them.
    const fromRegistry = registered?.[locale];
    if (fromRegistry)
        return fromRegistry;
    if (locale === FALLBACK_LOCALE)
        return fr;
    const page = readPageTexts();
    return page && page.locale === locale ? page.texts : undefined;
}
/** French: the floor of every lookup, and the only dictionary the browser always has. */
export function fallbackDictionary() {
    return fr;
}
//# sourceMappingURL=bundled-registry.js.map